import { IDEA_EVENT_TYPE } from '@/constants/idea'
import service from '@/service'
import dedup from '@/store/dedup'
import urlManager from '@/urlManager'
import makeOwnerships from '@/utils/makeOwnerships'
import * as R from 'ramda'

const mutations = {
  SET_IDEA_LIST: 'SET_IDEA_LIST',
  SET_IDEA: 'SET_IDEA',
  DELETE_IDEA_LIST: 'DELETE_IDEA_LIST',
  DELETE_IDEA: 'DELETE_IDEA'
}

/**
 * This is the store module for ideaLists and ideas
 */
export default {
  namespaced: true,
  state: () => ({
    ideaList: {},
    ideas: {}
  }),
  getters: {
    ideaListById: state => id => state.ideaList[id],
    ideaById: state => id => state.ideas[id]
  },
  mutations: {
    [mutations.SET_IDEA_LIST](state, ideaList) {
      state.ideaList = { ...state.ideaList, [ideaList.id]: ideaList }
    },
    [mutations.SET_IDEA](state, idea) {
      state.ideas = { ...state.ideas, [idea.id]: idea }
    },
    [mutations.DELETE_IDEA_LIST](state, ideaListId) {
      state.ideaList = R.omit([ideaListId], state.ideaList)
    },
    [mutations.DELETE_IDEA](state, ideaId) {
      state.ideas = R.omit([ideaId], state.ideas)
    }
  },
  actions: {
    async getIdeaLists({ commit }, { orgId, teamId, scopeId }) {
      const response = await service.client.get(urlManager.ideaList(orgId), {
        params: {
          team_id: teamId,
          scope_id: scopeId
        }
      })
      response.data.forEach(ideaList => {
        commit(mutations.SET_IDEA_LIST, ideaList)
      })
      return response.data
    },
    async getIdeaList({ commit }, { orgId, listId }) {
      return await dedup('getIdeaList', { orgId, listId }, async () => {
        const response = await service.client.get(urlManager.ideaListDetails(orgId, listId))
        commit(mutations.SET_IDEA_LIST, response.data)
        return response.data
      })
    },
    async createIdeaList({ commit }, payload) {
      const { orgId, name, teamId } = payload
      const reqBody = {
        type: teamId ? 'team' : 'organization',
        teamId: teamId,
        name: name
      }
      const response = await service.client.post(urlManager.ideaList(orgId), reqBody, {
        params: {
          team_id: teamId
        }
      })
      commit(mutations.SET_IDEA_LIST, response.data)
      return response
    },
    async patchIdeaList({ commit }, { orgId, id, data }) {
      let response = await service.client.patch(urlManager.ideaListDetails(orgId, id), data)
      commit(mutations.SET_IDEA_LIST, response.data)
      return response.data
    },
    async getIdeas(options, { orgId, ideaListId }) {
      const response = await service.client.get(urlManager.ideasList(orgId, ideaListId))
      const ideas = response.data
      ideas.forEach(idea => {
        options.commit(mutations.SET_IDEA, idea)
      })
      return ideas
    },
    async getIdea(options, { orgId, id }) {
      return await dedup('getIdea', { orgId, id }, async () => {
        const response = await service.client.get(urlManager.ideaDetails(orgId, id))
        options.commit(mutations.SET_IDEA, response.data)
        return response.data
      })
    },
    async createIdea(options, payload) {
      const { orgId, listId, title, impact, reach, effort, ownerId, coOwnerIds } = payload
      let response = await service.client.post(urlManager.allIdeas(orgId), {
        listId,
        title,
        impact,
        reach,
        effort,
        ownerships: makeOwnerships(orgId, ownerId, coOwnerIds)
      })
      options.commit(mutations.SET_IDEA, response.data)
      return response.data
    },
    async patchIdea(options, { orgId, id, data }) {
      if (data.ownerId || (data.coOwnerIds && data.coOwnerIds.length > 0)) {
        data = {
          ...R.omit(['ownerId', 'coOwnerIds'], data),
          ownerships: makeOwnerships(orgId, data.ownerId, data.coOwnerIds)
        }
      }
      let response = await service.client.patch(urlManager.ideaDetails(orgId, id), data)
      options.commit(mutations.SET_IDEA, response.data)
      return response.data
    },
    async deleteIdea(options, { orgId, id }) {
      await service.client.delete(urlManager.ideaDetails(orgId, id))
      options.commit(mutations.DELETE_IDEA, id)
    },
    async deleteIdeaList(options, { orgId, id }) {
      await service.client.delete(urlManager.ideaListDetails(orgId, id))
      options.commit(mutations.DELETE_IDEA_LIST, id)
    },
    async getIdeaEvents(options, { orgId, id }) {
      let response = await service.client.get(urlManager.ideaEvents(orgId, id))
      return response.data
    },
    async createIdeaEvent(options, { orgId, id, comment, actorId, commentType }) {
      let response = await service.client.post(urlManager.ideaEvents(orgId, id), {
        comment,
        commentType,
        ideaId: id,
        actorId,
        type: IDEA_EVENT_TYPE.COMMENT
      })
      return response.data
    },
    async updateIdeaEvent(options, { orgId, id, eventId, comment, commentType }) {
      let response = await service.client.put(urlManager.ideaEventDetails(orgId, id, eventId), {
        comment,
        commentType,
        type: IDEA_EVENT_TYPE.COMMENT
      })
      return response.data
    },
    async deleteIdeaEvent(options, { orgId, id, eventId }) {
      let response = await service.client.delete(urlManager.ideaEventDetails(orgId, id, eventId))
      return response.data
    }
  }
}
